import { JSX } from "@emotion/react/jsx-runtime";
import { Box, Card, CardContent, Typography, useTheme } from "@mui/material";
import { flexRender, type Row, type Table } from "@tanstack/react-table";
import { useVirtualizer } from "@tanstack/react-virtual";
import React from "react";
import { TABLE_MIN_HEIGHT } from "./constants";
import { EmptyState } from "./EmptyState";
import { MobileTableViewProps } from "./types";

const MOBILE_CARD_ESTIMATED_HEIGHT = 220;
const MOBILE_INITIAL_RENDER_COUNT = 8;
const MOBILE_VIRTUALIZATION_THRESHOLD = 50;

const MobileCard = React.memo(
  <T extends object>({
    row,
    table,
    onRowClick,
  }: {
    row: Row<T>;
    table: Table<T>;
    onRowClick?: (row: T) => void;
  }) => {
    const theme = useTheme();
    const cells = row.getVisibleCells();
    const titleCell =
      cells.find((c) => {
        const meta = c.column.columnDef.meta as
          | Record<string, unknown>
          | undefined;

        return meta?.["mobileTitle"] === true;
      }) ?? cells[0];
    const titleCellId = titleCell?.id;
    const remainingCells = cells.filter((c) => c.id !== titleCellId);

    return (
      <Card
        onClick={onRowClick ? () => onRowClick(row.original) : undefined}
        sx={{
          borderRadius: 2,
          border: `none`,
          cursor: onRowClick ? "pointer" : "default",
          boxShadow: "none",
          "-webkit-tap-highlight-color": "transparent",
        }}
        variant="outlined"
      >
        <CardContent
          sx={{
            p: 2,
            margin: 1,
            borderRadius: 2,
            boxShadow: "0px 0px 12px rgba(0, 0, 0, 0.1)",
            "&:last-child": { pb: 2 },
          }}
        >
          {titleCell && (
            <Box
              key={titleCell.id}
              sx={{
                fontSize: 18,
                fontWeight: 700,
                color: theme.colors.text[7],
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
            >
              {flexRender(
                titleCell.column.columnDef.cell,
                titleCell.getContext(),
              )}
            </Box>
          )}
          {remainingCells.map((cell) => {
            const visibleCellIndex = cells.indexOf(cell);
            const header =
              table.getHeaderGroups()[0]?.headers[visibleCellIndex];
            const headerText = header
              ? flexRender(header.column.columnDef.header, header.getContext())
              : "";

            return (
              <Box
                key={cell.id}
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  py: 0.5,
                  minHeight: 40,
                }}
              >
                {headerText && (
                  <Typography
                    sx={{
                      fontWeight: 700,
                      fontSize: 12,
                      color: theme.colors.text[1],
                      minWidth: "80px",
                    }}
                    variant="body2"
                  >
                    {headerText}
                  </Typography>
                )}
                <Box
                  sx={{
                    color: theme.colors.text[5],
                    fontWeight: 500,
                    fontSize: 14,
                    textAlign: "right",
                    flex: 1,
                    ml: headerText ? 2 : 0,
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                  }}
                >
                  {flexRender(cell.column.columnDef.cell, cell.getContext())}
                </Box>
              </Box>
            );
          })}
        </CardContent>
      </Card>
    );
  },
) as <T extends object>(props: {
  row: Row<T>;
  table: Table<T>;
  onRowClick?: (row: T) => void;
}) => JSX.Element;

(MobileCard as React.NamedExoticComponent).displayName = "MobileCard";

export const MobileTableView = <T extends object>(
  props: MobileTableViewProps<T>,
): JSX.Element => {
  const { table, onRowClick, virtualize = true, overscan = 8, height } = props;

  const rows = table.getRowModel().rows;

  const [scrollElement, setScrollElement] =
    React.useState<HTMLDivElement | null>(null);
  const [dynamicHeight, setDynamicHeight] =
    React.useState<number>(TABLE_MIN_HEIGHT);

  const shouldVirtualize =
    virtualize && rows.length >= MOBILE_VIRTUALIZATION_THRESHOLD;
  const viewportHeight = height ?? dynamicHeight;
  const numericHeight =
    typeof viewportHeight === "number" ? viewportHeight : TABLE_MIN_HEIGHT;

  React.useLayoutEffect(() => {
    if (height != null || !scrollElement) return;

    const updateHeight = () => {
      const rect = scrollElement.getBoundingClientRect();
      const available = window.innerHeight - rect.top;
      setDynamicHeight(Math.max(available, TABLE_MIN_HEIGHT));
    };

    updateHeight(); // Sync measure before paint

    let timerId: ReturnType<typeof setTimeout> | null = null;
    const resizeObserver = new ResizeObserver(() => {
      if (timerId) clearTimeout(timerId);
      timerId = setTimeout(updateHeight, 100);
    });

    resizeObserver.observe(scrollElement);
    window.addEventListener("resize", updateHeight);

    return () => {
      if (timerId) clearTimeout(timerId);
      resizeObserver.disconnect();
      window.removeEventListener("resize", updateHeight);
    };
  }, [height, scrollElement]);

  const rowVirtualizer = useVirtualizer({
    count: shouldVirtualize ? rows.length : 0,
    getScrollElement: () => scrollElement,
    estimateSize: () => MOBILE_CARD_ESTIMATED_HEIGHT,
    overscan,
    getItemKey: (index) => rows[index]?.id ?? index,
    enabled: shouldVirtualize && !!scrollElement,
    initialRect: { width: 0, height: numericHeight },
  });

  if (rows.length === 0) {
    return <EmptyState />;
  }

  if (!shouldVirtualize) {
    return (
      <Box sx={{ width: "100%", overflow: "hidden" }}>
        <Box sx={{ display: "flex", flexDirection: "column", gap: 1, pb: 2 }}>
          {rows.map((row) => (
            <MobileCard
              key={row.id}
              onRowClick={onRowClick}
              row={row}
              table={table}
            />
          ))}
        </Box>
      </Box>
    );
  }

  const virtualItems =
    scrollElement != null ? rowVirtualizer.getVirtualItems() : [];
  const initialRows = rows.slice(
    0,
    Math.min(rows.length, MOBILE_INITIAL_RENDER_COUNT),
  );

  return (
    <Box
      ref={setScrollElement}
      sx={{
        height: viewportHeight,
        width: "100%",
        overflow: "auto",
        WebkitOverflowScrolling: "touch",
      }}
    >
      {virtualItems.length > 0 ? (
        <Box
          sx={{
            height: rowVirtualizer.getTotalSize(),
            width: "100%",
            position: "relative",
          }}
        >
          {virtualItems.map((virtualRow) => {
            const row = rows[virtualRow.index];
            if (!row) return null;

            return (
              <Box
                data-index={virtualRow.index}
                key={row.id}
                ref={rowVirtualizer.measureElement}
                sx={{
                  position: "absolute",
                  top: 0,
                  left: 0,
                  width: "100%",
                  transform: `translateY(${virtualRow.start}px)`,
                  pb: 1,
                }}
              >
                <MobileCard onRowClick={onRowClick} row={row} table={table} />
              </Box>
            );
          })}
        </Box>
      ) : (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 1, pb: 2 }}>
          {initialRows.map((row) => (
            <MobileCard
              key={row.id}
              onRowClick={onRowClick}
              row={row}
              table={table}
            />
          ))}
        </Box>
      )}
    </Box>
  );
};
